import { environmentLabel } from './columns';
/**
 * GNB 오른쪽의 알림 종 — **돌아올 이유를 만드는 자리다.**
 *
 * 세 역할 실사용 검증이 셋 다 「내 티켓이 움직여도 아무것도 나를 부르지
 * 않는다」를 지적했다(2026-09-01). 이 종이 그 답이다: 안 읽은 개수를 달고,
 * 누르면 알림함이 열리고, 알림을 누르면 **그 티켓 모달이 바로 열린다**
 * (GNB 검색과 같은 길 — `App.tsx` 의 `TicketDialog`).
 *
 * **폴링이다 — 실시간 채널이 아니다.** 이 저장소에 WebSocket 이 없고,
 * 쓰는 사람이 셋인 도구에 그 층을 새로 들이는 비용이 「1분 늦게 안다」보다
 * 크다(칸반 알림은 초를 다투지 않는다). 그래서 1분마다 개수만 가볍게
 * 묻는다(`/notifications/unread-count`) — 목록은 열 때만 받는다.
 */

import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

import {
  errorMessage,
  listNotifications,
  NotLoggedInError,
  readAllNotifications,
  readNotification,
  unreadNotificationCount,
  type DeployEnvironment,
  type Notice,
  type TicketCol,
} from './api';
import { columnLabel } from './columns';
import { elapsed, formatWhen } from './format';

/** 개수 폴링 주기. 1분 — 칸반 알림은 초를 다투지 않고, 사람이 셋이라
    이보다 잦아도 서버만 바쁘다. */
const POLL_MS = 60_000;

/**
 * `col` 이 업무 칸인지 배포 환경인지는 `kind` 가 정하는데, `Notice.col` 은
 * 종류와 무관한 평면 유니언(`TicketCol | DeployEnvironment | null`)이라
 * switch 만으로는 TS 가 못 좁힌다. `kind` 로 먼저 가르고 값 비교로 좁힌다 —
 * 두 축의 값이 겹치지 않아 `as` 단언 없이 안전하다.
 */
function asWorkColumn(n: Notice): TicketCol | null {
  if (n.kind !== 'ticket.moved') return null;
  const { col } = n;
  return col === 'issue' || col === 'todo' || col === 'in_progress' || col === 'done'
    ? col
    : null;
}

/** 위 짝 — 배포 종류(`ticket.deployed`·`deploy.failed`)에서만 환경을 읽는다. */
function asDeployEnvironment(n: Notice): DeployEnvironment | null {
  if (!['ticket.deployed', 'deploy.failed', 'ticket.deploy_recorded', 'deploy.interrupted'].includes(n.kind)) return null;
  const { col } = n;
  return col === 'staging' || col === 'production' ? col : null;
}

/**
 * 알림 한 줄의 문장. **주어(한 사람)를 문장에 안 넣는다** — 한국어 조사
 * (가/이)가 이름 끝소리에 따라 갈려서, 넣으면 절반의 이름에서 문장이
 * 어색하다. 한 사람은 아래 메타 줄에 따로 적는다.
 */
export function noticeText(n: Notice): string {
  switch (n.kind) {
    case 'ticket.created':
      return '새 티켓이 왔다';
    case 'ticket.assigned':
      return '나에게 배정됐다';
    case 'ticket.moved': {
      const col = asWorkColumn(n);
      return `「${col ? columnLabel(col) : '?'}」 칸으로 옮겨졌다`;
    }
    case 'ticket.qa_changed':
      return `QA ${n.col === 'pending' ? '검증 요청·재검증' : n.col === 'passed' ? '통과' : n.col === 'failed' ? '실패·수정 요청' : '설정 변경'} — 티켓을 확인해 주세요`;
    case 'ticket.commented':
      return '댓글이 달렸다';
    case 'ticket.deploy_recorded':
      return `${environmentLabel(asDeployEnvironment(n)) ?? '?'} 배포 기록이 등록됐다 (실행 확인 아님)`;
    case 'deploy.interrupted':
      return `${environmentLabel(asDeployEnvironment(n)) ?? '?'} 배포가 중단됐다 — 실제 결과 확인 필요`;
    case 'ticket.deployed': {
      // 환경 이름(`staging`·`production`)은 이미 화면 표기 그대로라 —
      // `columnLabel` 같은 변환 없이 문자열을 그대로 쓴다.
      const env = asDeployEnvironment(n);
      return `${environmentLabel(env) ?? '?'} 으로 나갔다`;
    }
    case 'deploy.failed': {
      const env = asDeployEnvironment(n);
      return `${environmentLabel(env) ?? '?'} 배포가 실패했다`;
    }
    case 'ticket.mentioned':
      return '댓글에서 나를 불렀다';
  }
}

/** 「5분 전」. 시계가 어긋나 음수면 `elapsed` 가 0초로 접는다. */
function noticeWhen(n: Notice): string {
  const ago = elapsed(n.created_at, null);
  return ago === null ? formatWhen(n.created_at) : `${ago} 전`;
}

export function NotificationBell({
  onOpenTicket,
  onError,
  onUnauthorized,
}: {
  /** 고른 알림의 티켓을 **모달로** 연다 — GNB 검색과 같은 길이다. */
  onOpenTicket: (ticketId: number) => void;
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
}) {
  const [count, setCount] = useState(0);
  /** `null` 은 「아직 안 받았다」 — 빈 목록과 다르다. */
  const [items, setItems] = useState<Notice[] | null>(null);
  const [open, setOpen] = useState(false);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // 개수 폴링. **실패는 조용히 넘긴다** — 1분마다 도는 요청이라 토스트를
  // 띄우면 서버가 잠깐 흔들릴 때 화면이 오류로 덮인다. 로그인 만료만 알린다.
  useEffect(() => {
    let cancelled = false;
    async function tick() {
      try {
        const data = await unreadNotificationCount();
        if (!cancelled) setCount(data.count);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) onUnauthorized();
      }
    }
    void tick();
    const timer = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [onUnauthorized]);

  const refresh = useCallback(async () => {
    try {
      const data = await listNotifications();
      setItems(data.items);
      setMore(data.items.length === 30);
      setCount(data.unread);
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`알림을 불러오지 못했다: ${errorMessage(err)}`);
    }
  }, [onError, onUnauthorized]);

  async function loadMore() {
    if (loadingMore || !items?.length) return;
    setLoadingMore(true);
    try {
      const data = await listNotifications(items[items.length - 1].id);
      setItems((prev) => [...(prev ?? []), ...data.items]);
      setMore(data.items.length === 30);
    } catch (err) { onError(errorMessage(err)); }
    finally { setLoadingMore(false); }
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    // 열 때마다 새로 받는다 — 폴링은 개수만 알고 목록은 모른다.
    if (next) void refresh();
  }

  function openItem(n: Notice) {
    setOpen(false);
    onOpenTicket(n.ticket_id);
    if (n.read_at !== null) return;
    // 화면 먼저, 서버는 뒤에(낙관). 실패하면 다음에 열 때 안 읽음으로
    // 돌아올 뿐이라 사람을 부를 일이 아니다.
    const now = new Date().toISOString();
    setItems((prev) =>
      prev === null
        ? prev
        : prev.map((item) => (item.id === n.id ? { ...item, read_at: now } : item)),
    );
    setCount((c) => Math.max(0, c - 1));
    readNotification(n.id).catch((err: unknown) => {
      if (err instanceof NotLoggedInError) onUnauthorized();
    });
  }

  async function readAll() {
    try {
      await readAllNotifications();
      const now = new Date().toISOString();
      setItems((prev) =>
        prev === null
          ? prev
          : prev.map((item) => (item.read_at === null ? { ...item, read_at: now } : item)),
      );
      setCount(0);
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`읽음 처리에 실패했다: ${errorMessage(err)}`);
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {/* `.ib` 가 `position: relative` 라 배지가 그 안에 붙는다. */}
        <button
          type="button"
          className="ib"
          aria-label={count > 0 ? `알림 — 안 읽음 ${count}개` : '알림'}
        >
          <svg width="17" height="17" viewBox="0 0 17 17" fill="none" aria-hidden="true">
            <path
              d="M8.5 2.2a4.1 4.1 0 0 0-4.1 4.1v2.4l-1.2 2.5h10.6l-1.2-2.5V6.3a4.1 4.1 0 0 0-4.1-4.1Z"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinejoin="round"
            />
            <path
              d="M7 13.4a1.6 1.6 0 0 0 3 0"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          </svg>
          {count > 0 && (
            // 읽는 쪽에는 위 aria-label 이 이미 말했다 — 배지는 눈용이다.
            <span
              aria-hidden="true"
              className="num absolute right-0.5 top-0.5 grid h-[15px] min-w-[15px] place-items-center rounded-full bg-brand px-1 text-micro leading-none text-on-brand"
            >
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      {/* 390px 에서도 서야 한다 — 화면보다 넓어지지 않게 min() 으로 접는다. */}
      <PopoverContent
        align="end"
        sideOffset={6}
        className="w-[min(360px,calc(100vw-16px))] p-0"
      >
        <div className="flex items-center justify-between border-b border-line py-s1 pl-s4 pr-s2">
          {/* 값 하나를 가리키는 이름표다 — 소제목이 아니라서 굵게 안 세운다. */}
          <span className="text-meta text-ink-2">
            {count > 0 ? `안 읽음 ${count}` : '알림'}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={count === 0}
            onClick={() => void readAll()}
          >
            모두 읽음
          </Button>
        </div>
        <ul className="m-0 max-h-[400px] list-none divide-y divide-line overflow-y-auto p-0">
          {items === null ? (
            <li className="px-s4 py-s4 text-meta text-ink-2">불러오는 중…</li>
          ) : items.length === 0 ? (
            <li className="px-s4 py-s4 text-meta text-ink-2">알림이 없다</li>
          ) : (
            items.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  className="block w-full px-s4 py-s3 text-left hover:bg-accent"
                  onClick={() => openItem(n)}
                >
                  <span className="flex items-baseline gap-s2">
                    <span className="num text-micro text-ink-3">{n.ticket_key}</span>
                    {/* 읽음은 색으로 가른다 — 굵기 500/600 은 맑은고딕에 없다. */}
                    <span
                      className={`min-w-0 flex-1 truncate text-meta ${
                        n.read_at === null ? 'text-ink' : 'text-ink-2'
                      }`}
                    >
                      {n.ticket_title}
                    </span>
                    {n.read_at === null && (
                      <span
                        aria-hidden="true"
                        className="size-[6px] flex-none self-center rounded-full bg-brand"
                      />
                    )}
                  </span>
                  <span className="mt-s1 block text-micro text-ink-2">
                    {noticeText(n)}
                    {n.actor_name !== null && ` · ${n.actor_name}`}
                    {` · ${noticeWhen(n)}`}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
        {more && <Button className="w-full" variant="ghost" disabled={loadingMore} onClick={() => void loadMore()}>이전 알림 더 보기</Button>}
      </PopoverContent>
    </Popover>
  );
}
